import { useCallback, useEffect, useState } from 'react';
import type { Manifest, ManifestStatus } from '@/types/manifest';
import { getManifests } from '../api/manifests-api';

interface UseManifestsResult {
  manifests: Manifest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  backgroundRefetch: () => Promise<void>;
}

export function useManifests(status?: ManifestStatus, name?: string, dateFrom?: string, dateTo?: string): UseManifestsResult {
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchManifests = useCallback(async (background = false) => {
    if (!background) setIsLoading(true);
    setError(null);

    try {
      const response = await getManifests(status, name, dateFrom, dateTo);
      if (response.success && response.data) {
        // Sort manifests by status: Flagged first (needs review), then Open, InProgress, Complete
        const statusOrder: Record<ManifestStatus, number> = {
          Flagged: 1,
          Open: 2,
          InProgress: 3,
          Complete: 4,
        };

        const sortedManifests = response.data.sort((a, b) => {
          const orderA = statusOrder[a.status] || 999;
          const orderB = statusOrder[b.status] || 999;
          return orderA - orderB;
        });

        setManifests(sortedManifests);
      } else {
        setError(response.message || 'Failed to fetch manifests');
      }
    } catch {
      setError('An error occurred while fetching manifests');
    } finally {
      setIsLoading(false);
    }
  }, [status, name, dateFrom, dateTo]);

  useEffect(() => {
    fetchManifests();
  }, [fetchManifests]);

  return {
    manifests,
    isLoading,
    error,
    refetch: () => fetchManifests(false),
    backgroundRefetch: () => fetchManifests(true),
  };
}
